import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MapPin, Clock, Coins, Users } from "@phosphor-icons/react";
import { formatDateTime, formatCHF } from "../lib/api";
import { disciplineLabel } from "../lib/disciplines";

export default function CourseCard({ course, index = 0 }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage || "it";
  const seatsLeft = course.capacity - (course.bookings_count || 0);
  const sold = seatsLeft <= 0;
  return (
    <Link
      to={`/courses/${course.id}`}
      className="group block border border-white/10 bg-[#0A0A0A] hover:border-[#E30613]/60 transition-colors duration-300"
      style={{ animationDelay: `${index * 0.05}s` }}
      data-testid={`course-card-${course.id}`}
    >
      <div className="aspect-[16/10] relative overflow-hidden border-b border-white/10">
        <img
          src={course.image_url || "https://images.unsplash.com/photo-1526506118085-60ce8714f8c5?crop=entropy&cs=srgb&fm=jpg&q=70&w=900"}
          alt={course.title}
          className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
        <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-2 py-1 bg-[#E30613] text-white text-[10px] uppercase tracking-widest font-bold">
          {disciplineLabel(course.discipline, lang)}
        </div>
        {course.is_demo && (
          <div className="absolute top-3 right-3 px-2 py-1 bg-amber-500 text-black text-[10px] uppercase tracking-widest font-bold" data-testid="course-demo-badge">
            {t("demo.badge_short")}
          </div>
        )}
        {sold && !course.is_demo && (
          <div className="absolute top-3 right-3 px-2 py-1 bg-black border border-white/20 text-white text-[10px] uppercase tracking-widest font-bold">
            {t("common.complete")}
          </div>
        )}
        <div className="absolute bottom-3 left-3 right-3">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-300 font-bold">{formatDateTime(course.starts_at)}</div>
        </div>
      </div>

      <div className="p-5">
        <h3 className="font-display text-2xl uppercase font-black text-white leading-tight line-clamp-2 group-hover:text-[#E30613] transition-colors">
          {course.title}
        </h3>
        <div className="mt-1 text-[11px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">
          {t("common.with")} {course.instructor_name}
        </div>

        <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
          <div className="flex items-center gap-2 text-zinc-400">
            <MapPin size={14} className="text-[#E30613]" />
            <span className="truncate">{course.location}</span>
          </div>
          <div className="flex items-center gap-2 text-zinc-400">
            <Clock size={14} className="text-[#E30613]" />
            <span>{course.duration_min} {t("common.min")}</span>
          </div>
          <div className="flex items-center gap-2 text-zinc-400">
            <Users size={14} className="text-[#E30613]" />
            <span>{seatsLeft > 0 ? `${seatsLeft} ${t("common.seats")}` : t("common.complete")}</span>
          </div>
          <div className="flex items-center gap-2 text-white font-bold">
            <Coins size={14} className="text-[#E30613]" />
            <span>{formatCHF(course.price_chf)}</span>
          </div>
        </div>
      </div>
    </Link>
  );
}
